import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { getApiV1Url } from '../config/api';
import Header from '../components/Header/Header';
import LoadingSpinner from '../components/LoadingSpinner';
import ColumnFilter from '../components/Filters/ColumnFilter';
import { useColumnDrag } from '../hooks/useColumnDrag';
import Breadcrumbs from '../components/Breadcrumbs';
import * as XLSX from 'xlsx';
import './RosakkreditatsiyaPage.css';

interface CertificationBody {
  id: number;
  name: string | null;
  inn: string | null;
  certification_number: string | null;
  status: string | null;
  pipe_certification_standard: string | null;
  current_experts: string | null;
  experts_in_certificates: string | null;
  website: string | null;
  address: string | null;
  active_certificates_count: number | null;
  date_last_change: string | null;
  company_crm_id: string | null;
  director_os: string | null;
  contacts_os: string | null;
  period_suspension_oa: string | null;
  products_pp_rf_2425: string | null;
  date_stop_oa: string | null;
  date_change_oa: string | null;
  created_at: string;
  updated_at: string;
}

interface ColumnConfig {
  key: keyof CertificationBody;
  label: string;
  render?: (value: any, item: CertificationBody) => React.ReactNode;
}

const RosakkreditatsiyaOSPage: React.FC = () => {
  const [items, setItems] = useState<CertificationBody[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<keyof CertificationBody | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem('certification-body-visible-columns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        // ignore
      }
    }
    return [
      'name',
      'inn',
      'certification_number',
      'status',
      'active_certificates_count',
      'date_last_change',
      'address',
      'website',
      'pipe_certification_standard',
      'current_experts',
      'experts_in_certificates',
      'director_os',
      'contacts_os',
      'period_suspension_oa',
      'products_pp_rf_2425',
      'date_stop_oa',
      'date_change_oa',
      'company_crm_id',
      'created_at',
      'updated_at',
    ];
  });

  const defaultColumnOrder: string[] = [
    'name',
    'inn',
    'certification_number',
    'status',
    'active_certificates_count',
    'date_last_change',
    'address',
    'website',
    'pipe_certification_standard',
    'current_experts',
    'experts_in_certificates',
    'director_os',
    'contacts_os',
    'period_suspension_oa',
    'products_pp_rf_2425',
    'date_stop_oa',
    'date_change_oa',
    'company_crm_id',
    'created_at',
    'updated_at',
  ];

  const {
    columnOrder,
    draggedColumn,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    resetColumnOrder,
  } = useColumnDrag({
    defaultColumnOrder,
    storageKey: 'certification-body-column-order',
  });

  const [expandedCells, setExpandedCells] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const access = localStorage.getItem('access');
        const response = await axios.get<CertificationBody[]>(getApiV1Url('CERTIFICATION_BODY'), {
          headers: access ? { Authorization: `Bearer ${access}` } : {},
        });
        setItems(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Ошибка загрузки данных органов сертификации:', error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSort = useCallback((field: keyof CertificationBody) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  const sortedItems = useMemo(() => {
    if (!sortField) return items;

    return [...items].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue, 'ru')
          : bValue.localeCompare(aValue, 'ru');
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [items, sortField, sortDirection]);

  const getSortIcon = (field: keyof CertificationBody) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU');
    } catch {
      return dateString;
    }
  };

  const columns: ColumnConfig[] = [
    { key: 'name', label: 'Наименование' },
    { key: 'inn', label: 'ИНН' },
    { key: 'certification_number', label: 'Номер органа' },
    { key: 'status', label: 'Статус' },
    { key: 'active_certificates_count', label: 'Кол-во действующих СС' },
    { key: 'date_last_change', label: 'Дата последнего изменения', render: (value) => formatDate(value) },
    { key: 'address', label: 'Адрес' },
    {
      key: 'website',
      label: 'Сайт',
      render: (value) =>
        value ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : (
          '-'
        ),
    },
    { key: 'pipe_certification_standard', label: 'ОА (ГОСТ на труб)' },
    { key: 'current_experts', label: 'Эксперты сейчас на сайте' },
    { key: 'experts_in_certificates', label: 'Эксперты в сертификатах' },
    { key: 'director_os', label: 'Руководитель ОС' },
    { key: 'contacts_os', label: 'Контакты ОС' },
    { key: 'period_suspension_oa', label: 'Период приостановления ОА' },
    { key: 'products_pp_rf_2425', label: 'Продукция по ПП РФ 2425' },
    { key: 'date_stop_oa', label: 'Дата прекращения ОА', render: (value) => formatDate(value) },
    { key: 'date_change_oa', label: 'Даты изменения ОА' },
    {
      key: 'company_crm_id',
      label: 'Компания CRM',
      render: (value) => {
        if (!value) return '-';
        const stringValue = String(value);
        if (stringValue.startsWith('http://') || stringValue.startsWith('https://')) {
          return (
            <a href={stringValue} target="_blank" rel="noopener noreferrer">
              Ссылка
            </a>
          );
        }
        return stringValue;
      },
    },
    { key: 'created_at', label: 'Создано' },
    { key: 'updated_at', label: 'Обновлено' },
  ];

  const handleColumnVisibilityChange = (newVisible: string[]) => {
    setVisibleColumns(newVisible);
    localStorage.setItem('certification-body-visible-columns', JSON.stringify(newVisible));
  };

  const resetColumnSettings = () => {
    setVisibleColumns(defaultColumnOrder);
    resetColumnOrder();
    localStorage.setItem('certification-body-visible-columns', JSON.stringify(defaultColumnOrder));
  };

  const filteredColumnOrder = columnOrder.filter((key) => visibleColumns.includes(key));

  const getColumnByKey = (key: string) => columns.find((col) => col.key === key);

  const renderCellContent = (item: CertificationBody, column: ColumnConfig) => {
    const value = item[column.key];

    if (column.key === 'website' || column.key === 'company_crm_id') {
      return column.render ? column.render(value, item) : value ?? '-';
    }

    if (column.key === 'date_last_change' || column.key === 'date_stop_oa') {
      const formatted = column.render ? column.render(value as string, item) : formatDate(value as string);
      return formatted ?? '-';
    }

    if (column.key === 'active_certificates_count') {
      return value ?? '-';
    }

    const rawText = (value ?? '') as string;
    const cellId = `${item.id}-${column.key}`;
    const isExpanded = expandedCells[cellId];

    if (!rawText || rawText.length <= 100) {
      return rawText || '-';
    }

    const displayText = isExpanded ? rawText : `${rawText.slice(0, 100)}...`;

    return (
      <span>
        {displayText}{' '}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpandedCells((prev) => ({
              ...prev,
              [cellId]: !isExpanded,
            }));
          }}
          style={{
            border: 'none',
            background: 'none',
            padding: 0,
            margin: 0,
            cursor: 'pointer',
            color: '#007bff',
            fontSize: '12px',
          }}
        >
          {isExpanded ? 'Скрыть' : 'Подробнее'}
        </button>
      </span>
    );
  };

  const handleExportToExcel = () => {
    if (!items || items.length === 0) {
      return;
    }

    const exportData = items.map((item) => ({
      'Наименование': item.name ?? '',
      'ИНН': item.inn ?? '',
      'Номер органа': item.certification_number ?? '',
      'Статус': item.status ?? '',
      'Кол-во действующих СС': item.active_certificates_count ?? '',
      'Дата последнего изменения': formatDate(item.date_last_change),
      'Адрес': item.address ?? '',
      'Сайт': item.website ?? '',
      'ОА (ГОСТ на труб)': item.pipe_certification_standard ?? '',
      'Эксперты сейчас на сайте': item.current_experts ?? '',
      'Эксперты в сертификатах': item.experts_in_certificates ?? '',
      'Руководитель ОС': item.director_os ?? '',
      'Контакты ОС': item.contacts_os ?? '',
      'Период приостановления ОА': item.period_suspension_oa ?? '',
      'Продукция по ПП РФ 2425': item.products_pp_rf_2425 ?? '',
      'Дата прекращения ОА': formatDate(item.date_stop_oa),
      'Даты изменения ОА': item.date_change_oa ?? '',
      'Компания CRM': item.company_crm_id ?? '',
      'Создано': item.created_at ?? '',
      'Обновлено': item.updated_at ?? '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Органы по сертификации');
    XLSX.writeFile(workbook, 'organy_po_sertifikatsii.xlsx');
  };

  if (loading) {
    return (
      <>
        <Header />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <LoadingSpinner />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="navigation_certificates">
        <Breadcrumbs
          items={[
            { label: 'Главная страница', to: '/' },
            { label: 'Органы по сертификации' },
          ]}
        />
        <button
          type="button"
          className="download-button"
          onClick={handleExportToExcel}
        >
          Выгрузить в Excel
        </button>
      </div>
      <div className="rosakkreditatsiya-page">
        <div className="rosakkreditatsiya-header">
          <h1>Органы по сертификации</h1>
          <p className="rosakkreditatsiya-subtitle">Всего записей: {items.length}</p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
          <ColumnFilter
            columns={columns.map((c) => ({ key: c.key as string, label: c.label }))}
            visibleColumns={visibleColumns}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            defaultColumns={defaultColumnOrder}
            onReset={resetColumnSettings}
          />
        </div>

        <div className="rosakkreditatsiya-table-container certificates-table">
          <table className="rosakkreditatsiya-table">
            <thead>
              <tr>
                {filteredColumnOrder.map((columnKey) => {
                  const column = getColumnByKey(columnKey);
                  if (!column) return null;
                  return (
                    <th
                      key={columnKey}
                      className="sortable"
                      onClick={() => handleSort(column.key)}
                      draggable
                      onDragStart={(e) => handleDragStart(e, columnKey)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, columnKey)}
                      onDragEnd={handleDragEnd}
                      style={{
                        cursor: 'grab',
                        backgroundColor: draggedColumn === columnKey ? '#005a94' : undefined,
                        opacity: draggedColumn && draggedColumn !== columnKey ? 0.7 : 1,
                      }}
                    >
                      {column.label} {getSortIcon(column.key)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={filteredColumnOrder.length || 1} style={{ textAlign: 'center', padding: '20px' }}>
                    Нет данных для отображения
                  </td>
                </tr>
              ) : (
                sortedItems.map((item) => (
                  <tr key={item.id}>
                    {filteredColumnOrder.map((columnKey) => {
                      const column = getColumnByKey(columnKey);
                      if (!column) return null;
                      return (
                        <td key={columnKey}>
                          {renderCellContent(item, column)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default RosakkreditatsiyaOSPage;

